<script lang="ts" setup>
import type { AdminBreadcrumb, AdminNavItem } from '~kui/types/adminNav';

import { Icon } from '~ds/components/ui/icon';
import { SidebarMenuBadge } from '~ds/components/ui/sidebar';
import { ICONS } from '~ds/lib/icons';
import KUIContentPageShell from '~kui/components/KUI/Content/Layout/PageShell/KUIContentPageShell.vue';

import { useAdminNav } from '~/composables/useAdminNav';

interface Props {
  isContainer?: boolean;
  /** Crumbs a detail page appends to the nav-derived trail, e.g. the record it is showing. */
  extraCrumbs?: AdminBreadcrumb[];
}

withDefaults(defineProps<Props>(), { isContainer: true, extraCrumbs: () => [] });

const { nav } = await useAdminNav();
const route = useRoute();
const { code: clusterCode } = useCurrentCluster();

const activeRouteName = computed(() => String(route.name ?? ''));
const activeRoutePath = computed(() => route.path);
const rootTestId = computed(() => `page-${String(route.name)}`);

// `au-cluster` → `AU`, `us-cluster` → `US`. Falls back to plain "Admin" if
// the cluster hasn't been resolved (shouldn't happen on a real request).
const headerLabel = computed(() => {
  const id = clusterCode.value?.replace(/-cluster$/, '').toUpperCase();
  return id ? `Admin - ${id}` : 'Admin';
});
</script>

<template>
  <KUIContentPageShell
    :nav="nav"
    :active-route-name="activeRouteName"
    :active-route-path="activeRoutePath"
    :header-label="headerLabel"
    :is-container="isContainer"
    :root-test-id="rootTestId"
    :extra-crumbs="extraCrumbs"
  >
    <!-- Capability-locked destinations stay listed and clickable; the padlock says
         up front that the page will answer with the locked takeover. -->
    <template #nav-item-badge="{ item }: { item: AdminNavItem }">
      <SidebarMenuBadge
        v-if="item.isLocked"
        class="pointer-events-none text-muted-foreground opacity-50"
        :title="$t('content.locked.navItemTitle')"
      >
        <Icon
          :name="ICONS.lockSolid"
          class="size-3.5"
          aria-hidden="true"
        />
      </SidebarMenuBadge>
    </template>

    <template
      v-if="$slots.header"
      #header
    >
      <slot name="header" />
    </template>
    <template
      v-if="$slots['page-action']"
      #page-action
    >
      <slot name="page-action" />
    </template>

    <Suspense>
      <slot />
      <template #fallback>
        <KUICoreContainerCentered>
          <KUICoreLoaderAnyway :text="$t('general.common.loading')" />
        </KUICoreContainerCentered>
      </template>
    </Suspense>
  </KUIContentPageShell>
</template>
